/** @jsxImportSource @emotion/react */

import { memo } from "react";
import { FormattedMessage } from "react-intl";

import { Alert } from "@material-ui/lab";

import { alert, greyAlert } from "../../styles/layout";
import { Event, EventStatus } from "../../types";
import { isEventBeforePreviewAvailable, isEventPreviewAvailable } from "../../utils/eventHelpers";

interface Props {
  event: Event;
}
const EventStatusAlert = (props: Props) => {
  const { event } = props;
  const isPreparing = event && isEventBeforePreviewAvailable(event);
  const isBeforeStart = event.status === EventStatus.BeforeStart;
  const isPreviewAvailable = isEventPreviewAvailable(event);
  const isProcessing = event.status === EventStatus.Processing;
  const isServerOpen = event.status === EventStatus.ServerOpen;
  const isLive = event.status === EventStatus.Live;
  const isEnded = event.status === EventStatus.Ended;
  const isEncoding = event.status === EventStatus.Encoding;
  const isReadyForVod = event.status === EventStatus.ReadyForVOD;
  const isVod = event.status === EventStatus.Vod;
  const isVodStartTimePassed = Date.now() >= event.vod_started_at * 1000;
  const isEventAutomatic = event.video?.mode === "AUTOMATIC";

  // BVM-2180 vod to live(simulive) events has different alert messages
  if (event.vod_to_live) {
    if (isPreparing) {
      return (
        <Alert css={greyAlert} variant="filled">
          <FormattedMessage id="control-room-detail.alert.preparing-simulive" />
        </Alert>
      );
    }
    if (isBeforeStart) {
      return (
        <Alert css={greyAlert} variant="filled">
          <FormattedMessage id="control-room-detail.alert.processing-simulive" />
        </Alert>
      );
    }

    if (isProcessing) {
      return (
        <Alert css={greyAlert} variant="filled">
          <FormattedMessage id="control-room-detail.alert.processing-simulive" />
        </Alert>
      );
    }

    if (isServerOpen) {
      return (
        <Alert css={greyAlert} variant="filled">
          <FormattedMessage id="control-room-detail.alert.preview-simulive" />
        </Alert>
      );
    }
  }

  if (isPreparing) {
    return (
      <Alert css={greyAlert} variant="filled">
        <FormattedMessage id="control-room-detail.alert.preparing" />
      </Alert>
    );
  }

  if (isPreviewAvailable && !isEventAutomatic) {
    return (
      <Alert css={alert} variant="filled" severity="info">
        <FormattedMessage id="control-room-detail.alert.preview-available" />
      </Alert>
    );
  }

  if (isProcessing) {
    return (
      <Alert css={greyAlert} variant="filled">
        <FormattedMessage id="control-room-detail.alert.processing" />
      </Alert>
    );
  }

  if (isServerOpen) {
    return (
      <Alert css={alert} variant="filled" severity="info">
        <FormattedMessage id="control-room-detail.alert.preview" />
      </Alert>
    );
  }

  if (isLive) {
    return (
      <Alert css={alert} variant="filled" severity="success">
        <FormattedMessage id="control-room-detail.alert.on-air" />
      </Alert>
    );
  }

  if ((isEncoding || isReadyForVod || isVod) && !isVodStartTimePassed) {
    return (
      <Alert css={greyAlert} variant="filled">
        <FormattedMessage id="control-room-detail.alert.encoding" />
      </Alert>
    );
  }

  if (isEncoding && isVodStartTimePassed) {
    return (
      <Alert css={alert} variant="filled" severity="warning">
        <FormattedMessage id="control-room-detail.alert.encoding" />
      </Alert>
    );
  }
  if (isVod && isVodStartTimePassed) {
    return (
      <Alert css={alert} variant="filled" severity="success">
        <FormattedMessage id="control-room-detail.alert.VOD" />
      </Alert>
    );
  }
  if (isEnded) {
    return (
      <Alert css={greyAlert} variant="filled">
        <FormattedMessage id="control-room-detail.alert.finished" />
      </Alert>
    );
  }

  return null;
};

export default memo(EventStatusAlert);
